<h1>{{ 'Settings' | translate }}</h1>

@if (settingsReady$ | async) {
  @if (processing.settings$ | async; as settings) {
    @if ((processing.isInUpload$ | async) === false) {
      <k-details [title]="'Background' | translate">
        <color-chrome
          (onChange)="setBackgroundColor($event.color.rgb)"
          [color]="settings.localSettings.background.color"
        />

        <k-labelled-checkbox
          [label]="'Gradient-brightness effect' | translate"
          (checkedChange)="setBackgroundEffect($event)"
          [startingValue]="settings.localSettings.background.effect"
        />

        <k-button (click)="resetBackground()" type="outlined-primary">
          {{ 'Reset background' | translate }}
        </k-button>
      </k-details>
      <k-details [title]="'Lights' | translate">
        <app-entity-feature-settings-lights></app-entity-feature-settings-lights>
      </k-details>
      <k-details [title]="'Preview and initial perspective' | translate">
        <p class="wizard-step-text">
          {{ 'Click on this image to set a nice and fitting camera perspective' | translate }}
        </p>
        <img
          class="previewimage"
          [src]="settings.localSettings.preview | fixImageUrl"
          (click)="setInitialPerspectivePreview()"
        />
      </k-details>

      <k-button style="align-self: center" rounded type="solid-primary" (click)="exportSettings()">
        <mat-icon style="margin-right: 4px">download</mat-icon>
        <span>{{ 'Download viewer settings' | translate }}</span>
      </k-button>

      @if (!(isStandalone$ | async)) {
        <k-button (click)="backToDefaultSettings()" type="outlined-primary">
          {{ 'Back to default' | translate }}
        </k-button>
        @if (canSaveSettings$ | async) {
          <k-button (click)="saveSettings()" type="solid-primary">
            {{ 'Save as default' | translate }}
          </k-button>
        }
      }
    }
    @if (processing.isInUpload$ | async) {
      <k-wizard direction="vertical" [linear]="true" #stepper>
        @if (processing.hasMeshSettings$ | async) {
          <k-wizard-step [label]="'Mesh settings' | translate">
            <app-entity-feature-settings-mesh></app-entity-feature-settings-mesh>
            <k-button type="solid-primary" (click)="showNextAlertFirstStep()">{{
              'Save mesh settings' | translate
            }}</k-button>
          </k-wizard-step>
        }
        <k-wizard-step [label]="'Background and lights' | translate">
          <div class="wizard-step-content">
            <k-details [title]="'Background' | translate">
              <color-chrome
                (onChange)="setBackgroundColor($event.color.rgb)"
                [color]="settings.localSettings.background.color"
              />

              <k-labelled-checkbox
                [label]="'Gradient-brightness effect' | translate"
                (checkedChange)="setBackgroundEffect($event)"
                [startingValue]="settings.localSettings.background.effect"
              />

              <k-button (click)="resetBackground()" type="outlined-primary">
                {{ 'Reset background' | translate }}
              </k-button>
            </k-details>

            <k-details [title]="'Lights' | translate">
              <app-entity-feature-settings-lights></app-entity-feature-settings-lights>
            </k-details>
          </div>

          <k-button type="solid-primary" (click)="stepper.nextStep()">{{
            'Save background and lights' | translate
          }}</k-button>
        </k-wizard-step>
        @if ((processing.mediaType$ | async) !== 'audio') {
          <k-wizard-step [label]="'Preview and initial perspective' | translate">
            <div class="wizard-step-content wizard-step-content-dark">
              <p class="wizard-step-text">
                {{
                  'Please select a nice and fitting camera perspective for your object by moving it to the desired position. Click “Save preview” to set the current view as initial perspective. - Smile!'
                    | translate
                }}
              </p>
            </div>

            <k-button type="solid-primary" (click)="setPerspectiveStep()">{{
              'Save preview and initial perspective' | translate
            }}</k-button>
          </k-wizard-step>
        }
        @if (!(isStandalone$ | async)) {
          <k-wizard-step [label]="'Confirm' | translate">
            <div class="wizard-step-content wizard-step-content-dark">
              <p class="wizard-step-text">
                {{ 'You set all settings and we celebrate it with this preview.' | translate }}
              </p>

              <img class="previewimage" [src]="settings.localSettings.preview | fixImageUrl" />

              <p class="wizard-step-text">
                {{
                  'Please save the settings if you are done. Or go back to make changes. Afterwards you may add annotations.'
                    | translate
                }}
              </p>
            </div>

            <k-button type="outlined-primary" (click)="stepper.prevStep()">{{
              'Back' | translate
            }}</k-button>

            <k-button type="solid-primary" (click)="saveSettings()">{{
              'Save' | translate
            }}</k-button>
          </k-wizard-step>
        } @else {
          <k-wizard-step [label]="'Export' | translate">
            <div class="wizard-step-content wizard-step-content-dark">
              <p class="wizard-step-text">
                {{
                  'Your settings can now be exported and loaded in the standalone Kompakkt viewer.'
                    | translate
                }}
              </p>

              <img class="previewimage" [src]="settings.localSettings.preview | fixImageUrl" />
            </div>
            <k-button type="outlined-primary" (click)="stepper.prevStep()">{{
              'Back' | translate
            }}</k-button>
            <k-button type="solid-primary" (click)="exportSettings()">{{
              'Export' | translate
            }}</k-button>
          </k-wizard-step>
        }
      </k-wizard>
    }
  }
}

<div
  class="video-preview-generation-overlay"
  [class.visible]="videoPreviewGenerationState() || videoPreviewUrl()"
  [class.dimmed-bg]="videoGenerationSuccess()"
>
  <div class="centered-content">
    <h2>{{ 'Preview' }}</h2>

    <p>
      {{ 'Generating preview image and video. This should only take a moment...' | translate }}
    </p>

    <div class="stack">
      @if (videoPreviewGenerationState(); as state) {
        <div
          class="progress-container"
          [style.--width]="state.current / state.total"
          [style.opacity]="videoGenerationSuccess() ? 0 : 1"
        >
          <div class="progress-bar"></div>
        </div>
      }

      @if (videoGenerationSuccess(); as success) {
        @if (success.value) {
          <p class="success-message">{{ 'Video generation successful!' | translate }}</p>
        } @else {
          <p class="error-message">
            {{ 'Video generation failed. Only using preview screenshot.' | translate }}
          </p>
        }
      }
    </div>

    <div class="video-container" [style.--bg-color]="currentBackgroundColor$ | async">
      @if (videoPreviewUrl(); as url) {
        <video
          #videoEl
          class="video-preview"
          [src]="url"
          muted
          loop
          playsinline
          preload="auto"
          (mousemove)="updateVideoPreview(videoEl, $event)"
          (mouseleave)="resetVideoPreview(videoEl)"
        ></video>
      }
    </div>

    <k-button
      [disabled]="!videoGenerationSuccess()"
      (click)="closeVideoPreviewOverlay()"
      type="solid-primary"
    >
      {{ 'Close' | translate }}
    </k-button>
  </div>
</div>
